import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  FiLogOut,
  FiSettings,
  FiAlignCenter,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';

import { IoIosList, IoIosContrast } from 'react-icons/io';

import { signOut } from 'store/modules/auth/actions';
import {
  switchTheme,
  toggleBar,
  changeEmissora,
} from 'store/modules/usuario/actions';

import useOutsideClick from 'utils/hooks/useOutsideClick';
import { Role } from 'utils/variables';

import Select from 'components/Select';
import AsyncSelect from 'components/AsyncSelect';

import Avatar from 'components/Avatar';
import { Container, Profile, MenuList, Item, Form } from './styles';

import noAvatar from 'assets/avatar.png';
import { IRootState } from 'store/modules/rootReducer';
import { FormHandles } from '@unform/core';
import Button from 'components/Button';
import Novu from 'components/Novu';
import api from 'services/api';

interface IEmissoraProps {
  id: number;
  nome: string;
}

interface IComercialItem {
  id: number;
  titulo: string;
  codmaterial: number;
  categoria: string;
  formatted_created_at: string;
}

interface ISelectedComercial {
  value: string | number;
  titulo: string;
}

const Header: React.FC = () => {
  /**
   * Use State
   */
  const [visible, setVisible] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const ref = useRef(null);
  const { isDarkTheme } = useSelector((state: any) => state.usuario);

  const formRef = useRef<FormHandles>(null);

  const navigate = useNavigate();

  /**
   * REDUX
   **/
  const { emissora, profile, isSideBarMini } = useSelector(
    (state: IRootState) => state.usuario,
  ) as any;
  const dispatch = useDispatch();
  const activePageName = useLocation().pathname.replace('/', '');

  const options = useMemo(
    () =>
      profile && profile.emissoras && profile.emissoras.length > 0
        ? profile.emissoras.map((emi: IEmissoraProps) => ({
            value: emi.id,
            label: emi.nome,
          }))
        : [],
    [profile],
  );

  useOutsideClick(ref, () => {
    if (visible) setVisible(false);
  });

  const handleToggleVisible = useCallback(() => {
    setVisible(!visible);
  }, [visible]);

  const handleSignOut = useCallback(() => {
    dispatch(signOut());
  }, [dispatch]);

  const handleSwitchTheme = useCallback(() => {
    dispatch(switchTheme());
  }, [dispatch]);

  const handleTogglerBar = useCallback(() => {
    dispatch(toggleBar());
  }, [dispatch]);

  const handleSubmit = useCallback(
    (value: any) => {
      dispatch(changeEmissora(value));
    },
    [dispatch],
  );

  const handlePerfil = useCallback(() => {
    navigate('/perfil');
  }, [navigate]);

  const handleSearch = useCallback(
    () => setSearchOpen(prevState => !prevState),
    [],
  );

  const handleLoadComercial = useCallback(async (value: string) => {
    setAsyncLoading(true);
    if (value.length > 2) {
      try {
        const response = await api.get<IComercialItem[]>('comerciaists', {
          params: { search: value },
        });
        if (response.data) {
          return response.data.map(comercial => ({
            value: comercial.id,
            label: comercial.codmaterial + ' - ' + comercial.titulo,
            sublabel:
              comercial.categoria + ' - ' + comercial.formatted_created_at,
            ...comercial,
          }));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setAsyncLoading(false);
      }
    }
  }, []);

  const data = { emissora };
  return (
    <Container $mini={isSideBarMini}>
      <nav>
        <button onClick={handleTogglerBar}>
          {isSideBarMini ? <IoIosList /> : <FiAlignCenter />}
        </button>

        <span>{activePageName}</span>
      </nav>
      <Form
        ref={formRef}
        $searchopen={searchOpen}
        initialData={data}
        onSubmit={() => {}}
      >
        {searchOpen ? (
          <AsyncSelect
            searchable
            name="comercial"
            loadOptions={handleLoadComercial}
            placeholder="Digite o CM ou titulo do comercial"
            type="threeOptions"
            isClearable
            onChange={(comercial: ISelectedComercial) => {
              if (comercial) {
                if (formRef.current) {
                  formRef.current.setFieldValue('comercial', '');
                }
                navigate(`/historico/${comercial.value}`);
              }
            }}
            isMulti={false}
            //  isDisabled={loading || !!filtro.retranca}

            isLoading={asyncLoading}
          />
        ) : (
          ([
            'dashboard',
            'player',
            'dashboard/comerciaisgravados',
            'dashboard/comerciaisnaogravados',
            'dashboard/midiasavulsas',
            'dashboard/comerciaissemplanilha',
            'dashboard/chamadasaprovadas',
            'dashboard/chamadasvalidas',
            'materiais',
            'roteiros',
            'eleicoes/painel',
            'eleicoes/mapademidia',
            'dashboard/comerciaisautorizadossemplanilha',
            'dashboard/comerciaisindisponiveis',
            'orientacoesexibicao',
          ].includes(activePageName) ||
            activePageName.includes('exclusao')) && (
            <Select
              placeholder="Selecione..."
              name="emissora"
              onChange={handleSubmit}
              type="default"
              options={options}
            />
          )
        )}
      </Form>
      <aside>
        <Button
          fill
          disabled={
            !profile ||
            ![Role.ADMIN, Role.OPEC, Role.EXIB, Role.ENG].includes(profile.role)
          }
          round
          color="font"
          tooltip={
            searchOpen ? 'Fechar busca de comercial' : 'Buscar comercial'
          }
          tooltipPosition="left"
          onClick={handleSearch}
        >
          {searchOpen ? <FiX /> : <FiSearch />}
        </Button>
        <Novu theme={isDarkTheme ? 'dark' : 'light'} />
        {!!profile && (
          <Profile onClick={handleToggleVisible} ref={ref}>
            <Avatar
              name={profile.nome}
              size="36"
              src={profile.avatar_url || noAvatar}
              alt={profile.login}
              round
            />
            <b />

            <MenuList $visible={visible}>
              <Item>
                <button onClick={handlePerfil}>
                  <FiSettings />
                  <span>Perfil</span>
                </button>
              </Item>
              <Item onClick={handleSwitchTheme}>
                <button>
                  <IoIosContrast />
                  <span>Alterar Tema</span>
                </button>
              </Item>
              <Item>
                <button onClick={handleSignOut}>
                  <FiLogOut />
                  <span>Sair</span>
                </button>
              </Item>
            </MenuList>
          </Profile>
        )}
      </aside>
    </Container>
  );
};

export default Header;
